import React from "react";
import type { Project } from "../../api/useProjects";
import { stableColor } from "@ui";
import { useMutateBody } from "../../api/useQuery";

type FailureColorsType = "FAILURE" | "MANUAL_FAILURE";

export function getFailureColor(project: Project, type: FailureColorsType, failure: string) {
  if (type === "MANUAL_FAILURE")
    return project.manualFailureColors[failure] || stableColor(failure);
  return project.failureColors[failure] || stableColor(failure);
}

function setFailureColor(
  nameFailure: string,
  newColor: string,
  setting: Record<string, string>,
): Record<string, string> {
  if (newColor === stableColor(nameFailure)) {
    // Default Color
    return Object.fromEntries(Object.entries(setting).filter(key => key[0] !== nameFailure));
  } else {
    // Custom Color
    return { ...setting, [nameFailure]: newColor };
  }
}

function useMutateFailureColor(project: Project) {
  const mutate = useMutateBody("/projects");

  const mutateGlobalFailureColors = React.useCallback(
    (type: FailureColorsType, nameFailure: string, color: string) => {
      const failureColors =
        type === "FAILURE"
          ? setFailureColor(nameFailure, color, project.failureColors)
          : project.failureColors;

      const manualFailureColors =
        type === "MANUAL_FAILURE"
          ? setFailureColor(nameFailure, color, project.manualFailureColors)
          : project.manualFailureColors;

      mutate(({ collection, ...rest }) => ({
        collection: collection.map(p =>
          p.id === project.id ? { ...p, failureColors, manualFailureColors } : p,
        ),
        ...rest,
      }));
    },
    [project, mutate],
  );

  return mutateGlobalFailureColors;
}

export default useMutateFailureColor;
